import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationKeys } from '@kidshome/api-contract';

/**
 * When a Web Push arrives with the app OPEN, the service worker posts a `notification-received` message
 * to its window clients (see `sw.ts`). This hook listens for it and invalidates the unread-count + list
 * queries, so the bell badge + `/notifications` center refresh immediately rather than waiting for the
 * ~60s visible-only poll / focus refetch (ADR-0135 §8). Additive + best-effort — a closed app just relies
 * on the poll on next open. Mount once per shell (both are inside the QueryClientProvider).
 */
export function usePushNotificationInvalidation(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    const handler = (event: MessageEvent): void => {
      const data = event.data as { type?: string } | undefined;
      if (data?.type === 'notification-received') {
        void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        void queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [queryClient]);
}
